import React, { useEffect, useState } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { DashboardHeader } from "../../dashboards/components/DashboardHeader";
import { useSubheader } from "../../../../_metronic/layout";
import { getCallAgentAnalytics, getDashboardBatchList, getDashboardUserList, getDashboardIndustryList } from "../../dashboards/redux/dashboardApi";

// Import modular components
import AnalyticsFilters from "../components/AnalyticsFilters";
import AnalyticsCards from "../components/AnalyticsCards";
import CallStatusDistributionChart from "../components/CallStatusDistributionChart";
import CountryPerformance from "../components/CountryPerformance";
import WebsitePerformance from "../components/WebsitePerformance";
import SalesTeamPerformance from "../components/SalesTeamPerformance";
import RecentActivities from "../components/RecentActivities";
import OutboundCalls from "../components/outboundCalls";
import CallDurationCards from "../components/CallDurationCards";

export function AnalyticsDashboard() {
  const subheader = useSubheader();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const { user } = useSelector((state) => state.auth, shallowEqual);
  const restrictCreatedByToSelf =
    user &&
    user._id &&
    user.roleId != 1 &&
    user.email != "himanshu.soni@webcluesinfotech.com";

  // Initialize agent state from URL query parameter
  const getAgentFromUrl = () => {
    const params = new URLSearchParams(location.search);
    const agentParam = params.get("agent");
    const validAgents = ["inquery", "incomingleads", "oldleads", "newleads", "oldleads_newleads", "all"];
    return validAgents.includes(agentParam) ? agentParam : "inquery";
  };

  const [agent, setAgent] = useState(getAgentFromUrl);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Sync URL when agent state changes (only depend on agent, not location.search,
  // to avoid re-running when history.replace itself updates the URL)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("agent") !== agent) {
      params.set("agent", agent);
      history.replace({ search: params.toString() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent]);

  // Declare filter selection states
  const [selectedUser, setSelectedUser] = useState(restrictCreatedByToSelf ? (user?._id || "all") : "all");
  const [selectedBatch, setSelectedBatch] = useState("all");
  const [selectedIndustry, setSelectedIndustry] = useState("all");

  const handleAgentChange = (newAgent) => {
    setAgent(newAgent);
    setSelectedUser(restrictCreatedByToSelf ? (user?._id || "all") : "all");
    setSelectedBatch("all");
    setSelectedIndustry("all");
  };

  const handleUserChange = (newUser) => {
    setSelectedUser(newUser);
    setSelectedBatch("all");
  };

  // Declare dropdown options states
  const [userOptions, setUserOptions] = useState([]);
  const [batchOptions, setBatchOptions] = useState([]);
  const [industryOptions, setIndustryOptions] = useState([]);

  const { callAgentAnalytics, isCallAgentAnalyticsLoading } = useSelector(
    (state) => state.dashboard,
    shallowEqual
  );
  const activeUserFilter = restrictCreatedByToSelf ? (user?._id || "all") : selectedUser;

  useEffect(() => {
    subheader.setTitle("AI Calling Analytics Dashboard");
  }, [subheader]);

  // Fetch User List once when the agent changes to Agent 2 or Agent 4
  useEffect(() => {
    setUserOptions([]);
    if (!restrictCreatedByToSelf && (agent === "oldleads" || agent === "newleads" || agent === "oldleads_newleads")) {
      dispatch(getDashboardUserList(agent)).then((userData) => {
        if (userData) {
          const formattedUsers = [
            { value: "all", label: "All Users" },
            ...(userData.users || []).map((u) => ({ value: u._id, label: u.name || "Unknown" }))
          ];
          setUserOptions(formattedUsers);
        }
      });
    }
  }, [dispatch, agent, restrictCreatedByToSelf]);

  // Fetch Industry List once when the agent changes to Agent 4 (newleads)
  useEffect(() => {
    setIndustryOptions([]);
    if (agent === "newleads") {
      dispatch(getDashboardIndustryList(agent)).then((industryData) => {
        if (industryData) {
          const formattedIndustries = [
            { value: "all", label: "All Industries" },
            ...(industryData.industryList || []).map((ind) => ({ value: ind.Industry, label: ind.IndustryName || ind.Industry }))
          ];
          setIndustryOptions(formattedIndustries);
        }
      });
    }
  }, [dispatch, agent]);

  // Fetch Batch list whenever agent OR activeUserFilter changes
  useEffect(() => {
    setBatchOptions([]);
    if (agent === "oldleads" || agent === "newleads" || agent === "oldleads_newleads") {
      dispatch(getDashboardBatchList(agent, activeUserFilter)).then((batchData) => {
        if (batchData) {
          const formattedBatches = [
            { value: "all", label: "All Batches" },
            ...(batchData.batchNames || []).map((b) => ({ value: b, label: b }))
          ];
          setBatchOptions(formattedBatches);
        }
      });
    }
  }, [dispatch, agent, activeUserFilter]);

  // Fetch Analytics stats whenever filters change
  useEffect(() => {
    // If only the first date (start date) is selected, do not trigger API call until the ending date is also selected
    if (startDate && !endDate) {
      return;
    }

    let params = {
      agent,
      user: activeUserFilter,
      batchName: selectedBatch,
      industry: selectedIndustry
    };

    if (startDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      params.startDate = start.getTime();
    }
    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      params.endDate = end.getTime();
    }

    dispatch(getCallAgentAnalytics(params));
  }, [dispatch, startDate, endDate, agent, activeUserFilter, selectedBatch, selectedIndustry]);

  const getSelectedRangeLabel = () => {
    const opt = { day: "numeric", month: "short", year: "numeric" }; // e.g. "Apr 1, 2026"
    if (startDate && endDate) {
      return `${startDate.toLocaleDateString("en-US", opt)} - ${endDate.toLocaleDateString("en-US", opt)}`;
    }
    if (startDate) {
      return `${startDate.toLocaleDateString("en-US", opt)}`;
    }
    return "All Date";
  };

  const stats = callAgentAnalytics || null;
  const hideAnalyticsSections = false;

  return (
    <div className="test-dashboard-container">
      <DashboardHeader title="" />

      <div className="dashboard">
        {/* Render Filters Component */}
        <AnalyticsFilters
          agent={agent}
          setAgent={handleAgentChange}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          selectedUser={selectedUser}
          setSelectedUser={handleUserChange}
          selectedBatch={selectedBatch}
          setSelectedBatch={setSelectedBatch}
          selectedIndustry={selectedIndustry}
          setSelectedIndustry={setSelectedIndustry}
          userOptions={userOptions}
          batchOptions={batchOptions}
          industryOptions={industryOptions}
          showUserDropdown={!restrictCreatedByToSelf}
        />

        {/* Render KPI Cards & their loading state directly below filters */}
        {isCallAgentAnalyticsLoading ? (
          <div className="card card-custom gutter-b mb-6">
            <div className="card-body">
              <div className="text-center py-5">
                <div className="spinner spinner-primary spinner-lg mr-15"></div>
                <span className="text-muted ml-5">Loading KPI statistics...</span>
              </div>
            </div>
          </div>
        ) : (
          !hideAnalyticsSections && (
            <AnalyticsCards
              stats={stats}
              agent={agent}
              selectedRangeLabel={getSelectedRangeLabel()}
              totalLeads={stats?.totalLeads ?? null}
            />
          )
        )}

        {/* OutboundCalls and CallStatusDistributionChart row is kept permanently mounted
            so OutboundCalls never unmounts/remounts when isCallAgentAnalyticsLoading toggles,
            preventing duplicate callAgentStatsGraph API calls. */}
        {(agent === "inquery" || agent === "incomingleads" || agent === "oldleads" || agent === "newleads" || agent === "oldleads_newleads" || agent === "all") && (
          <div className="py-1 mt-6">
            <div className="row mb-6 align-items-stretch">
              <div className="col-12 col-lg-6 mb-4 mb-lg-0 d-flex flex-column">
                <OutboundCalls
                  agent={agent}
                  startDate={startDate}
                  endDate={endDate}
                  user={restrictCreatedByToSelf ? (user?._id || "all") : selectedUser}
                  batchName={selectedBatch}
                  industry={selectedIndustry}
                />
              </div>
              <div className="col-12 col-lg-6 d-flex flex-column">
                <CallStatusDistributionChart agent={agent} stats={stats} />
              </div>
            </div>
          </div>
        )}

        {/* Secondary analytics sections: Call Duration, Country/Sales performance, etc. */}
        {isCallAgentAnalyticsLoading ? (
          <div className="row mt-3">
            <div className="col-12">
              <CallDurationCards stats={null} loading={true} />
            </div>
          </div>
        ) : (
          (agent === "inquery" || agent === "incomingleads" || agent === "oldleads" || agent === "newleads" || agent === "oldleads_newleads" || agent === "all") && stats && (
            <>
              {!hideAnalyticsSections && (
                <div className="py-1">
                  {/* Render Call Duration Cards for All Agents */}
                  <div className="row mb-6">
                    <div className="col-12">
                      <CallDurationCards stats={stats} loading={false} />
                    </div>
                  </div>

                  <div className="row mb-6 align-items-stretch">
                    <div className="col-12 col-lg-6 mb-4 mb-lg-0 d-flex flex-column">
                      {/* Render Country Performance Section */}
                      <CountryPerformance stats={stats} />
                    </div>
                    {(agent === "inquery" || agent === "incomingleads") && (
                      <div className="col-12 col-lg-6 d-flex flex-column">
                        <WebsitePerformance stats={stats} />
                      </div>
                    )}
                    {(agent === "oldleads" || agent === "newleads" || agent === "oldleads_newleads") && (
                      <div className="col-12 col-lg-6 d-flex flex-column">
                        <SalesTeamPerformance stats={stats} />
                      </div>
                    )}
                  </div>

                  {(agent === "oldleads" || agent === "newleads" || agent === "oldleads_newleads") && (
                    <div className="row mb-6">
                      <div className="col-12 col-lg-6">
                        <RecentActivities stats={stats} agent={agent} />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )
        )}
      </div>
    </div>
  );
}